import React, { forwardRef, useId } from 'react'

/**
 * Composite text input: label + input + optional error.
 *
 * Props:
 *   - label, value, onChange, type, placeholder, disabled, required
 *   - error: string — when present, sets aria-invalid and renders the error message
 *   - readOnly: boolean — switches to the locked-bg variant
 *   - autoFocus, name, id, ...rest — passed to the underlying <input>
 *
 * Refs forward to the underlying <input> so callers can manage focus
 * (e.g., a Modal's `initialFocusRef`).
 */
export const TextField = forwardRef(function TextField(
  {
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    disabled = false,
    required = false,
    error,
    readOnly = false,
    id,
    className = '',
    ...rest
  },
  ref,
) {
  const generatedId = useId()
  const inputId = id || generatedId
  const errorId = `${inputId}-error`
  const hasError = Boolean(error)
  return (
    <div className={['field', className].filter(Boolean).join(' ')}>
      {label ? (
        <label
          className="label"
          htmlFor={inputId}
          data-required={required || undefined}
        >
          {label}
        </label>
      ) : null}
      <input
        ref={ref}
        id={inputId}
        type={type}
        className="input"
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        readOnly={readOnly}
        data-readonly={readOnly || undefined}
        aria-required={required || undefined}
        aria-invalid={hasError || undefined}
        aria-describedby={hasError ? errorId : undefined}
        {...rest}
      />
      {hasError ? (
        <span id={errorId} role="status" className="field-error">
          {error}
        </span>
      ) : null}
    </div>
  )
})
